import type { ISODateTimeString } from '@gat-ship/types'
import { Temporal } from '@js-temporal/polyfill'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { getCurrentTimeZoneId, toAnchorTime } from './date-utils'
import type { DateInputCalendarModelValue } from '../../date-calendar/DateCalendar.vue'

/** Time-of-day a picked, date-only value is anchored to within the day. */
export type DatePickerAnchorTime = 'midnight' | 'noon' | 'now' | 'end-of-day'

/** Props for {@link useDatePicker}. */
export interface UseDatePickerProps {
  /** The bound value — a UTC instant string, or `undefined` when empty. */
  modelValue: MaybeRefOrGetter<ISODateTimeString | undefined>
  /** Timezone the date is displayed and interpreted in. */
  timeZone: MaybeRefOrGetter<string | undefined>
  /**
   * Time-of-day a picked date is anchored to. When `undefined`, the
   * model's existing time-of-day is kept instead.
   */
  anchorTime: MaybeRefOrGetter<DatePickerAnchorTime | undefined>
}

/**
 * Bridges an `ISODateTimeString` model and `DateCalendar`'s
 * `PlainDate` model: exposes the selected day for the calendar's
 * `model-value`, a human-readable label, and a `handleDateChange` that
 * turns a picked `PlainDate` into an instant and commits it via
 * `updateModel`.
 *
 * A picked date is anchored to `anchorTime` (e.g. noon) when set;
 * otherwise the model's existing time-of-day is kept. It takes plain
 * props and a plain `updateModel` callback, so it can back any
 * calendar-based date input.
 */
export function useDatePicker(
  props: UseDatePickerProps,
  updateModel: (value: ISODateTimeString | undefined) => void
) {
  const modelValue = computed(() => toValue(props.modelValue))
  const timeZone = computed(
    () => toValue(props.timeZone) ?? getCurrentTimeZoneId()
  )
  const anchorTime = computed(() => toValue(props.anchorTime))

  /** The currently selected date. */
  const selectedDate = computed(() => {
    if (!modelValue.value) {
      return undefined
    }
    return Temporal.Instant.from(modelValue.value)
      .toZonedDateTimeISO(timeZone.value)
      .toPlainDate()
  })

  /** A human-readable label for the selected date. */
  const datePickerLabel = computed(() => {
    if (!modelValue.value) {
      return 'Pick a date'
    }
    return Temporal.Instant.from(modelValue.value).toLocaleString(
      Intl.DateTimeFormat().resolvedOptions().locale,
      {
        dateStyle: 'long',
        hour12: false
      }
    )
  })

  function handleDateChange(date: DateInputCalendarModelValue) {
    if (!date) return

    // `mode="single"` always yields a PlainDate; a DateRange means the
    // calendar was misconfigured.
    if ('from' in date) {
      console.warn('useDatePicker received a DateRange in single mode')
      return
    }
    const plainDate = date as Temporal.PlainDate

    // Keep the model's existing time-of-day; a fresh value leaves the
    // omitted time fields to default to 0.
    const current = modelValue.value
      ? Temporal.Instant.from(modelValue.value).toZonedDateTimeISO(
          timeZone.value
        )
      : undefined
    const picked = Temporal.ZonedDateTime.from({
      year: plainDate.year,
      month: plainDate.month,
      day: plainDate.day,
      hour: current?.hour,
      minute: current?.minute,
      second: current?.second,
      millisecond: current?.millisecond,
      microsecond: current?.microsecond,
      nanosecond: current?.nanosecond,
      timeZone: timeZone.value
    })

    // Anchor the time-of-day when requested, so the committed instant
    // sits on a stable point of the day (noon survives timezone shifts).
    const anchored = anchorTime.value
      ? toAnchorTime(picked, anchorTime.value, timeZone.value)
      : picked

    updateModel(anchored.toInstant().toString() as ISODateTimeString)
  }

  return {
    selectedDate,
    datePickerLabel,
    handleDateChange
  }
}
